import type { ClickOutside } from './utils.d.js';

/**
 * Provides an action for a Svelte element that handles clicks and keypresses outside of the element.
 * @param {HTMLElement} element
 * @param {() => void} callbackFunction
 * @returns
 */
export function clickOutside(element: HTMLElement, callback: (boolean?) => void, dsDocument: Document = document): ClickOutside {
	function onClick(event) {
		if (!element.contains(event.target)) {
			callback();
		}
	}

	function onEscape(event) {
		if (event.key === 'Escape') {
			callback(true);
		}
	}

	// To include outside clicks, we can't discard the listener after the first click...
	dsDocument.body.addEventListener('click', onClick);
	// ...but we can discard the listener after the first `Esc`-keypress.
	dsDocument.body.addEventListener('keydown', onEscape, true);

	return {
		update(newCallback) {
			callback = newCallback;
		},
		destroy() {
			dsDocument.body.removeEventListener('click', onClick);
			dsDocument.body.removeEventListener('keydown', onEscape, true);
		},
	};
}

/**
 * Retrieves the edition of the page either from a pageConfig or the URL. Falls back to 'at' if no edition is found.
 * @returns {'at' | 'de'}
 */
export function getEdition(dstWindow: Window = window): 'at' | 'de' {
	return dstWindow.DERSTANDARD?.pageConfig?.edition ?? (dstWindow.location.origin.endsWith('.de') ? 'de' : 'at');
}

/**
 * Used to compare update states with the current state, to evaluate if an update is necessary.
 *
 * @param {Array<T>} a first array
 * @param {Array<T>} b second array
 * @returns {boolean} Whether the two arrays are equal
 */
export function arraysAreEqual<T>(a: T[], b: T[]): boolean {
	return a && b && a.length === b.length && a.every((value, index) => objectsAreEqual(value, b[index]));
}

/**
 * @param {T} a first object
 * @param {T} b second object
 * @returns {boolean} whether the two objects are equal
 */
function objectsAreEqual<T>(a: T, b: T): boolean {
	return Object.keys(a).every((key) => a[key] === b[key]);
}

/**
 * Used to compare two timestamps to determine, whether a fetch is required
 * @param storageKey to the timestamp of the last fetch
 * @returns
 */
export function shouldFetch(listName: string, isManualReload: boolean): boolean {
	const savedToStorage = parseInt(sessionStorage.getItem(`${listName}-timestamp`), 10);
	const nonUser = sessionStorage.getItem(`${listName}-user`);

	if (isManualReload) {
		return true;
	}

	// Prevet fetch, if user is assumed not to use this feature.
	if (nonUser) {
		return false;
	}

	// This condition is fulfilled, if no entry can be retrieved from sessionStorage
	// If an unknown key is used, sessionStorage.getItem() returns null --> parseInt(null) is NaN
	if (Number.isNaN(savedToStorage)) {
		return true;
	}

	const now = Date.now();
	const elapsed = now - savedToStorage;

	return elapsed > 3 * 60 * 1000; // Three minutes
}
